import { useEffect, useRef, useState } from 'react';
import ErrorBoundaryWrapper from 'components/ErrorBoundaryWrapper';
import theme from 'config/theme';
import Error from 'components/Error';
import { Link } from 'components/Link';
import Spacing from 'components/Spacing';
import { Body } from 'components/Typography';
import { ModalComponent } from 'components/Modal';
import { SlideStories } from 'components/SlideStories';
import { ProfileCard } from 'components/Cards/ProfileCard';
import { SupportOathCTA } from 'components/SupportOathCTA';
import { trackPixel } from 'helpers/pixel';
import { useAppSelector } from 'helpers/hooks';
import { getUserState } from 'selectors/user';
import { getPagesState } from 'selectors/pages';
import { getSubscriptionsData, getSubscriptionsSummary } from 'selectors/subscriptions';
import { setIsUserOnboarded, setUserName } from 'services/storage';
import { useLogout } from 'hooks/useLogout';
import { Network } from './Network';
import { AccountInfo } from './AccountInfo';
import { DonationHistory, DonationHistoryTab } from './DonationHistory';
import { MonthlyRecurringDonations } from './MonthlyRecurringDonations';
import { LeftContent } from './styled';
import { getUserStripeSubscriptionsData } from 'selectors/userStripeSubscriptions';
import { StripeRecurringDonations } from './StripeRecurringDonations';
import { Toast } from 'components/Toast';
import { useGateValue, useStatsigClient } from '@statsig/react-bindings';
import { useReadyGate } from 'hooks/useReadyGate';
import { CycleSection } from './CycleSection';
import { BREAKPOINTS } from 'constants/breakpoints';
import { LLMSection } from './LLMSection';
import { DonorThanksBanner } from './DonorThanksBanner';

const SHOW_LLM_CARD_GATE = 'show_llm_card_user_profile';
const SHOW_DONOR_THANKS_BANNER_GATE = 'show_donor_thanks_banner_user_profile';
const IS_COMMUNITY_IMPACT_REPORT_READY = false;

const defaultModalStyles = {
  width: 'fit-content',
  padding: 0,
  height: 'auto',
  maxHeight: 915 as number | string,
  border: 'none',
};

export const ProfileScreen = () => {
  const { handleLogout } = useLogout();
  const { isLoading: isStatsigLoading } = useStatsigClient();
  const showLLMCard = useGateValue(SHOW_LLM_CARD_GATE);
  const showDonorThanksBanner = useReadyGate(SHOW_DONOR_THANKS_BANNER_GATE) === 'on';
  const networkRef = useRef(null);
  const historyRef = useRef(null);
  const settingsRef = useRef(null);
  const [isSlideVisible, setIsSlideVisible] = useState(false);
  const [topIssues, setTopIssues] = useState(Array<string>());
  const [modalStyles, setModalStyles] = useState(defaultModalStyles);
  const [historyTab, setHistoryTab] = useState<DonationHistoryTab>('byDonation');
  const [showCanceledDESubscriptions, setShowCanceledDESubscriptions] = useState(false);
  const [wasDECardShownActive, setWasDECardShownActive] = useState(false);
  const {
    userState,
    pagesState,
    subscriptionsData,
    subscriptionsSummary,
    stripeSubscriptionsData,
  } = useAppSelector(state => ({
    userState: getUserState(state),
    pagesState: getPagesState(state),
    subscriptionsData: getSubscriptionsData(state),
    subscriptionsSummary: getSubscriptionsSummary(state),
    stripeSubscriptionsData: getUserStripeSubscriptionsData(state),
  }));
  const hasActiveDESubscriptions = subscriptionsData?.some(
    subscription => subscription.state === 'active'
  );
  const hasAllCanceledDESubscriptions = !hasActiveDESubscriptions && !!subscriptionsData?.length;
  const hasStripeSubscriptions = !!stripeSubscriptionsData?.length;
  const hasActiveStripeSubscriptions = !!stripeSubscriptionsData?.find(
    subscription => subscription.status === 'active'
  );

  useEffect(() => {
    trackPixel('universalPixel');
  }, []);

  useEffect(() => {
    if (!userState.isLoading && userState.data) {
      const isOnboarded = userState.data.is_onboarded;
      setIsUserOnboarded(isOnboarded.toString());
      setUserName(userState.data?.first_name);
    }
  }, [userState]);

  const handleScroll = (place: string) => {
    let newRef: any = null;
    if (place === 'network') newRef = networkRef.current;
    if (place === 'history') newRef = historyRef.current;
    if (place === 'settings') newRef = settingsRef.current;
    if (!newRef) return;
    // Not offsetTop: every target sits inside a position: relative ProfileCard, so
    // it would measure from the card rather than the document.
    window.scrollTo({
      top: newRef.getBoundingClientRect().top + window.scrollY - 200,
      left: 0,
      behavior: 'smooth',
    });
  };

  const scrollToHistory = (tab?: DonationHistoryTab) => {
    if (tab) setHistoryTab(tab);
    handleScroll('history');
  };

  useEffect(() => {
    if (!pagesState.isLoading && pagesState.data) {
      setTopIssues(
        pagesState.data.pages
          .filter(page => userState?.data?.issues?.includes(page.query_str))
          .map(item => item.value)
      );
    }
  }, [pagesState, userState?.data?.issues]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= BREAKPOINTS.MOBILE || window.innerHeight <= 700) {
        setModalStyles({
          ...defaultModalStyles,
          height: '100%',
          maxHeight: 'inherit',
        });
      } else {
        setModalStyles(defaultModalStyles);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (hasActiveDESubscriptions) {
      setWasDECardShownActive(true);
    }
  }, [hasActiveDESubscriptions]);

  const toggleShowCanceledDESubscriptions = () => {
    setShowCanceledDESubscriptions(prevState => !prevState);
  };

  return (
    <>
      <Toast />
      <ModalComponent
        isOpen={isSlideVisible}
        closeModal={() => setIsSlideVisible(false)}
        style={modalStyles}
        contentLabel="SlideStories"
        withCustomStyle
        iconColor={theme.colors.white}
      >
        <SlideStories
          isSlideVisible={isSlideVisible}
          showSlides={setIsSlideVisible}
          isCommunityReportReady={IS_COMMUNITY_IMPACT_REPORT_READY}
        />
      </ModalComponent>
      <div className="cards-container">
        <LeftContent $width="100%">
          {showDonorThanksBanner && <DonorThanksBanner />}
          <ProfileCard smallTitle title="Your Cycle So Far" withoutPadding={true}>
            <CycleSection
              isCommunityReportReady={IS_COMMUNITY_IMPACT_REPORT_READY}
              onScrollToHistory={scrollToHistory}
              showImpactSlides={() => {
                setIsSlideVisible(true);
              }}
            />
          </ProfileCard>
          {!isStatsigLoading && showLLMCard && (
            <>
              <Spacing $size={40} />
              <ProfileCard smallTitle title="Ask us anything">
                <LLMSection />
              </ProfileCard>
            </>
          )}
          <Spacing $size={40} />
          <ProfileCard smallTitle title="Support Oath">
            <SupportOathCTA
              variant="light"
              eyebrow="Fund The Research"
              headline="Keep the recommendations coming."
              body="Oath's research and vetting run on separate support from donors like you, not a share of what you give to candidates. Would you join the 80% of donors that support our work directly?"
              buttonText="Yes, count me in"
              placement="profile"
              disclaimer="Supports Oath's operations. Not a contribution to any candidate or committee."
            />
          </ProfileCard>
          <Spacing $size={40} />
          {hasStripeSubscriptions && (
            <>
              <ProfileCard smallTitle title="Your Recurring Donations">
                <ErrorBoundaryWrapper
                  fallback={
                    <Error
                      data={{ component: 'StripeSubscriptionManagement' }}
                      errorMsg="Failure with profile StripeSubscriptionManagement component"
                      shouldAlert={true}
                    >
                      <Body>
                        There was an error fetching your recurring donation history. Please contact{' '}
                        support.
                      </Body>
                    </Error>
                  }
                >
                  <StripeRecurringDonations subscriptions={stripeSubscriptionsData} />
                </ErrorBoundaryWrapper>
              </ProfileCard>
              {hasAllCanceledDESubscriptions && !wasDECardShownActive && (
                <button
                  className="profile-toggle-de-subs-button"
                  type="button"
                  onClick={toggleShowCanceledDESubscriptions}
                  aria-expanded={showCanceledDESubscriptions}
                >{`${showCanceledDESubscriptions ? 'Hide' : 'Show'} additional canceled subscriptions`}</button>
              )}
              <Spacing $size={40} />
            </>
          )}
          {(hasActiveDESubscriptions ||
            wasDECardShownActive ||
            showCanceledDESubscriptions ||
            (hasAllCanceledDESubscriptions && !hasStripeSubscriptions)) && (
            <>
              <ProfileCard smallTitle title="Your Monthly Recurring Donations">
                <ErrorBoundaryWrapper
                  fallback={
                    <Error
                      data={{ component: 'SubscriptionManagement' }}
                      errorMsg="Failure with profile SubscriptionManagement component"
                      shouldAlert={true}
                    >
                      <Body>
                        There was an error fetching your subscription history. Please contact{' '}
                        support.
                      </Body>
                    </Error>
                  }
                >
                  <MonthlyRecurringDonations
                    subscriptions={subscriptionsData}
                    totalActiveSubscriptions={subscriptionsSummary.total_active_count}
                    totalAmount={subscriptionsSummary.total_active_amount}
                  />
                </ErrorBoundaryWrapper>
              </ProfileCard>
              <Spacing $size={40} />
            </>
          )}
          <ProfileCard smallTitle title="Your Network Impact Summary">
            <div ref={networkRef} />
            <Network />
          </ProfileCard>
          <Spacing $size={40} />
          <ProfileCard smallTitle title="Your Donation History">
            <div ref={historyRef} />
            <ErrorBoundaryWrapper
              fallback={
                <Error
                  data={{ component: 'DonationHistory' }}
                  errorMsg="Failure with profile DonationHistory component"
                  shouldAlert={true}
                >
                  <Body>
                    There was an error fetching your donation history. Please contact support.
                  </Body>
                </Error>
              }
            >
              <DonationHistory activeTab={historyTab} onTabChange={setHistoryTab} />
            </ErrorBoundaryWrapper>
          </ProfileCard>
          <Spacing $size={40} />
          <ProfileCard smallTitle title="Your Account Info & Settings">
            <div ref={settingsRef} />
            {userState.data && (
              <AccountInfo
                topIssues={topIssues}
                hasActiveStripeSubscriptions={hasActiveStripeSubscriptions}
              />
            )}
          </ProfileCard>
          <div>
            <Spacing $size={35} />
            <Link onClick={handleLogout} $color={theme.colors.shaft}>
              Log Out
            </Link>
          </div>
        </LeftContent>
      </div>
    </>
  );
};
